import type { AuthenticatedUserContract, Locale, SystemStatusContract } from "@chams-pay/contracts";
import { translate } from "@chams-pay/contracts";
import { Badge } from "@chams-pay/design-system/components/badge";
import { Button } from "@chams-pay/design-system/components/button";
import { Label } from "@chams-pay/design-system/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@chams-pay/design-system/components/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@chams-pay/design-system/components/table";
import { KeyRound } from "lucide-react";
import type { ReactNode } from "react";
import { formatStamp, initialOf } from "../../core/format.js";
import { deploymentProductName } from "../../core/deployment.js";
import {
  useAccessCodes,
  useInstallationRequests,
  useSystemStatus,
} from "../../core/use-operations.js";
import { Bidi } from "../../ui/figures.js";
import { SchoolSettingsPanel } from "../../ui/school-settings.js";
import { AccessCodeReveal, ScopedError, SkeletonLines } from "../../ui/states.js";

function Section({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h3 className="chams-eyebrow">{title}</h3>
      {children}
    </section>
  );
}

function StatusRow({
  name,
  detail,
  ready,
  retryLabel,
  onRetry,
}: {
  readonly name: string;
  readonly detail: ReactNode;
  readonly ready: boolean;
  readonly retryLabel: string;
  readonly onRetry?: () => void;
}) {
  return (
    <div
      data-ready={ready}
      className="flex min-h-14 flex-wrap items-center gap-3 border-b border-border/60 px-4 py-2.5 last:border-0"
    >
      <span
        aria-hidden="true"
        className={`size-2 shrink-0 rounded-full ring-3 ${
          ready ? "bg-success ring-success/15" : "bg-warning ring-warning/15"
        }`}
      />
      <strong className="font-heading text-[12.5px] font-bold">{name}</strong>
      <span className="ms-auto flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
        {detail}
        {!ready && onRetry && (
          <Button type="button" variant="outline" size="sm" className="min-h-11" onClick={onRetry}>
            {retryLabel}
          </Button>
        )}
      </span>
    </div>
  );
}

function SystemStatusSection({ locale }: { readonly locale: Locale }) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const { status, failed, reload } = useSystemStatus();

  return (
    <Section title={t("ops.systemStatus")}>
      <div data-testid="status-screen">
        {status ? (
          <div className="service-status-list overflow-hidden rounded-lg border border-border bg-card">
            <StatusRow
              name={t("ops.service")}
              ready={status.state === "ready"}
              retryLabel={t("ops.retry")}
              onRetry={reload}
              detail={<ServiceDetail status={status} />}
            />
            <StatusRow
              name={t("ops.worker")}
              ready={status.worker.ready}
              retryLabel={t("ops.retry")}
              onRetry={reload}
              detail={
                status.worker.heartbeatAt ? (
                  <Bidi>{formatStamp(status.worker.heartbeatAt, locale)}</Bidi>
                ) : (
                  <span className={status.worker.ready ? "" : "text-warning"}>
                    {t("settings.unavailable")}
                  </span>
                )
              }
            />
          </div>
        ) : failed ? (
          <ScopedError locale={locale} onRetry={reload} />
        ) : (
          <div className="rounded-lg border border-border bg-card p-5">
            <SkeletonLines />
          </div>
        )}
      </div>
    </Section>
  );
}

function ServiceDetail({ status }: { readonly status: SystemStatusContract }) {
  return (
    <Bidi>
      v{status.application.version} · {status.database.datasetVersion}
    </Bidi>
  );
}

function InstallationRequestsSection({
  locale,
  csrf,
}: {
  readonly locale: Locale;
  readonly csrf: string;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const requests = useInstallationRequests(csrf);

  return (
    <Section title={t("ops.deviceRequests")}>
      <div data-testid="installation-requests">
        {requests.loading ? (
          <div className="rounded-lg border border-border bg-card p-5">
            <SkeletonLines />
          </div>
        ) : requests.failed ? (
          <ScopedError locale={locale} onRetry={requests.reload} />
        ) : requests.items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-surface-2 px-4 py-5 text-center text-xs text-muted-foreground">
            {t("ops.deviceRequestsEmpty")}
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border bg-card">
            <Table className="min-w-[980px] table-fixed">
              <TableHeader>
                <TableRow className="border-border/70 bg-surface-2 hover:bg-surface-2">
                  <TableHead className="chams-eyebrow h-10 w-[22%] text-start">
                    {t("ops.deviceRequestDevice")}
                  </TableHead>
                  <TableHead className="chams-eyebrow h-10 w-[18%] text-start">
                    {t("ops.forUser")}
                  </TableHead>
                  <TableHead className="chams-eyebrow h-10 w-[13%] text-start">
                    {t("ops.status")}
                  </TableHead>
                  <TableHead className="chams-eyebrow h-10 w-[15%] text-start">
                    {t("ops.deviceRequestAt")}
                  </TableHead>
                  <TableHead className="chams-eyebrow h-10 w-[17%] text-start">
                    {t("ops.deviceDecision")}
                  </TableHead>
                  <TableHead className="chams-eyebrow h-10 w-[15%] text-end">
                    {t("ops.actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.items.map((item) => {
                  const requester =
                    locale === "ar"
                      ? item.requestedBy.displayNameAr
                      : item.requestedBy.displayNameFr;
                  const decider = item.decidedBy
                    ? locale === "ar"
                      ? item.decidedBy.displayNameAr
                      : item.decidedBy.displayNameFr
                    : null;
                  const reference = item.id.replaceAll("-", "").slice(0, 6).toUpperCase();
                  const busy = requests.busyId === item.id;
                  const statusKey =
                    item.status === "pending"
                      ? "ops.deviceStatusPending"
                      : item.status === "authorized"
                        ? "ops.deviceStatusAuthorized"
                        : item.status === "rejected"
                          ? "ops.deviceStatusRejected"
                          : "ops.deviceStatusRevoked";
                  const statusVariant =
                    item.status === "pending"
                      ? "warning"
                      : item.status === "authorized"
                        ? "success"
                        : item.status === "rejected"
                          ? "destructive"
                          : "neutral";
                  return (
                    <TableRow key={item.id} data-status={item.status} className="border-border/60">
                      <TableCell className="py-3.5 text-start">
                        <span className="flex min-w-0 flex-col gap-0.5">
                          <Bidi className="truncate font-heading text-[12.5px] font-bold">
                            {item.label}
                          </Bidi>
                          <Bidi className="text-[10.5px] font-semibold text-muted-2">
                            {reference}
                          </Bidi>
                        </span>
                      </TableCell>
                      <TableCell className="py-3.5 text-start">
                        <span className="flex min-w-0 flex-col gap-0.5">
                          <strong className="truncate font-heading text-[12.5px]">
                            {requester}
                          </strong>
                          <Bidi className="text-[10.5px] font-semibold text-muted-2">
                            {item.requestedBy.identifier}
                          </Bidi>
                        </span>
                      </TableCell>
                      <TableCell className="py-3.5 text-start">
                        <Badge variant={statusVariant} dot>
                          {t(statusKey)}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-3.5 text-start">
                        <Bidi className="text-[11px] text-muted-foreground">
                          {formatStamp(item.requestedAt, locale)}
                        </Bidi>
                      </TableCell>
                      <TableCell className="py-3.5 text-start">
                        {item.decidedAt ? (
                          <span className="flex min-w-0 flex-col gap-0.5">
                            <Bidi className="text-[11px] text-muted-foreground">
                              {formatStamp(item.decidedAt, locale)}
                            </Bidi>
                            {decider && (
                              <span className="truncate text-[10.5px] font-semibold text-muted-2">
                                {decider}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-muted-2">—</span>
                        )}
                      </TableCell>
                      <TableCell className="py-3.5 text-end">
                        {item.status === "pending" ? (
                          <span className="inline-flex flex-nowrap justify-end gap-2">
                            <Button
                              type="button"
                              size="sm"
                              className="min-h-11 whitespace-nowrap"
                              disabled={Boolean(requests.busyId)}
                              onClick={() => void requests.approve(item.id)}
                            >
                              {t("ops.deviceRequestApprove")}
                            </Button>
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              className="min-h-11 whitespace-nowrap"
                              disabled={Boolean(requests.busyId)}
                              onClick={() => void requests.reject(item.id)}
                            >
                              {t("ops.deviceRequestReject")}
                            </Button>
                          </span>
                        ) : item.status === "authorized" ? (
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="min-h-11 whitespace-nowrap"
                            disabled={Boolean(requests.busyId)}
                            onClick={() => {
                              if (window.confirm(t("ops.deviceRevokeConfirm")))
                                void requests.revoke(item.id);
                            }}
                          >
                            {t("ops.deviceRequestRevoke")}
                          </Button>
                        ) : (
                          <span className="text-muted-2">—</span>
                        )}
                        {busy && (
                          <span className="ms-2 text-[11px] text-muted-foreground">
                            {t("ops.generating")}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </Section>
  );
}

function AccessCodeSection({ locale, csrf }: { readonly locale: Locale; readonly csrf: string }) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const codes = useAccessCodes(csrf);

  return (
    <Section title={t("ops.accessCodes")}>
      <form
        className="admin-code-card flex flex-col gap-3.5 rounded-lg border border-border bg-card p-4"
        onSubmit={(event) => {
          event.preventDefault();
          void codes.issue();
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Label className="flex flex-col items-stretch gap-1.5 text-start">
            <span className="font-heading text-[11px] font-bold text-foreground/75">
              {t("ops.forUser")}
            </span>
            <Select
              value={codes.identifier}
              onValueChange={(value) => codes.setIdentifier(value ?? codes.identifier)}
            >
              <SelectTrigger className="h-11 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="FA-LOCAL-001">FA-LOCAL-001</SelectItem>
                <SelectItem value="SA-LOCAL-001">SA-LOCAL-001</SelectItem>
              </SelectContent>
            </Select>
          </Label>
          <Label className="flex flex-col items-stretch gap-1.5 text-start">
            <span className="font-heading text-[11px] font-bold text-foreground/75">
              {t("ops.purpose")}
            </span>
            <Select
              value={codes.purpose}
              onValueChange={(value) => codes.setPurpose(value ?? codes.purpose)}
            >
              <SelectTrigger className="h-11 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="enrollment">{t("ops.purposePasskey")}</SelectItem>
                <SelectItem value="recovery">{t("admin.recovery")}</SelectItem>
              </SelectContent>
            </Select>
          </Label>
        </div>
        <Button type="submit" className="w-fit min-h-11" disabled={codes.busy}>
          <KeyRound aria-hidden="true" />
          {t("admin.issue")}
        </Button>
        {codes.issued && (
          <AccessCodeReveal locale={locale} code={codes.issued} expiresAt={codes.issuedExpiresAt} />
        )}
      </form>
    </Section>
  );
}

export function DesktopSettingsScreen({
  locale,
  user,
  csrf,
  onAdvanceYearActivated,
}: {
  readonly locale: Locale;
  readonly user: AuthenticatedUserContract | null;
  readonly csrf: string | null;
  readonly onAdvanceYearActivated?: () => void;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const isSystemAdministrator = user?.role === "system-administrator";
  const name = user
    ? locale === "ar"
      ? user.displayNameAr
      : user.displayNameFr
    : deploymentProductName;
  const role = isSystemAdministrator ? t("ops.roleAdmin") : t("profile.financialAdministrator");

  return (
    <section className="settings-screen flex flex-col gap-7" data-testid="settings-screen">
      <h2 className="font-heading text-lg font-bold">{t("ops.settings")}</h2>

      <Section title={t("ops.account")}>
        <div className="account-details flex flex-wrap items-center gap-3 rounded-lg border border-border bg-surface-2 p-4">
          <span
            aria-hidden="true"
            className="grid size-10 shrink-0 place-items-center rounded-full bg-teal-100 font-heading text-sm font-bold text-teal-700"
          >
            {initialOf(name)}
          </span>
          <span className="flex flex-col gap-0.5">
            <strong className="font-heading text-[13.5px] font-bold">{name}</strong>
            <Bidi className="text-[11px] font-semibold text-muted-2">
              {user?.identifier ?? "—"}
            </Bidi>
          </span>
          <Badge variant="info" className="ms-auto">
            {role}
          </Badge>
        </div>
      </Section>

      {user && csrf && (
        <Section title={t("settings.schoolSettings")}>
          <SchoolSettingsPanel
            locale={locale}
            csrf={csrf}
            role={user.role}
            {...(onAdvanceYearActivated ? { onAdvanceYearActivated } : {})}
          />
        </Section>
      )}
      {isSystemAdministrator && csrf && <InstallationRequestsSection locale={locale} csrf={csrf} />}
      {isSystemAdministrator && csrf && <AccessCodeSection locale={locale} csrf={csrf} />}
      {isSystemAdministrator && <SystemStatusSection locale={locale} />}
    </section>
  );
}
